import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
const DraggableHeaderWrapper = styled.div<{
  $is_dragging: boolean;
  $width?: number;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  min-width: 0;
  user-select: none;

  ${({
  $is_dragging,
  theme,
  $width
}) => $is_dragging && css`
      opacity: 0.5;
      background-color: ${theme.colors.surface.base.hover.neutral};
      ${$width && css`
        width: ${$width}px;
      `}
    `}
`;
const DragHandle = styled.div<{
  $is_dragging: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: ${({
  $is_dragging
}) => $is_dragging ? 'grabbing' : 'grab'};
  touch-action: none;

  &:hover {
    background-color: ${({
  theme,
  $is_dragging
}) => !$is_dragging && theme.colors.surface.base.hover.neutral};
  }
`;
const ResizeHandle = styled.div<{
  $is_resizing: boolean;
}>`
  position: absolute;
  top: 0;
  right: -3px;
  bottom: 0;
  width: 6px;
  z-index: 10;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 2px;
    height: 100%;
    background-color: transparent;
    transition: background-color 0.15s ease;
  }

  &:hover::after,
  ${({
  $is_resizing
}) => $is_resizing && css`
      &::after,
    `}
    &:active::after {
    background-color: ${({
  theme
}) => theme.colors.outline.focus.primary};
  }
`;
const ContentWrapper = styled.div<{
  $full_page: boolean;
  $is_first: boolean;
  $is_last: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: ${({
  theme
}) => theme.spacing(1)} ${({
  theme
}) => theme.spacing(2)};
  /* Content should not block drag handle */
  pointer-events: none;

  ${({
  theme,
  $full_page,
  $is_first,
  $is_last
}) => $full_page && css`
      ${$is_first && css`
        padding-left: ${theme.spacing(6)};
      `}
      ${$is_last && css`
        padding-right: ${theme.spacing(6)};
      `}
    `}

  /* Re-enable pointer events for interactive elements and prevent drag on them */
  button,
  a {
    pointer-events: auto;
    touch-action: auto;
  }
`;
interface DraggableColumnHeaderProps {
  id: string;
  children: React.ReactNode;
  disabled?: boolean;
  full_page?: boolean;
  is_first?: boolean;
  is_last?: boolean;
  configured_width?: number;
  resizable?: boolean;
  column_index?: number;
  stored_width?: number;
  is_any_resizing?: boolean;
  onResizeStart?: (column_index: number) => void;
  onResize?: (column_index: number, new_width: number) => void;
  onResizeEnd?: (column_index: number, new_width: number) => void;
}
const MIN_STORED_WIDTH = 100;
const MAX_STORED_WIDTH = 800;
const DraggableColumnHeader: React.FC<DraggableColumnHeaderProps> = t0 => {
  const $ = _c(57);
  const {
    id,
    children,
    disabled: t1,
    full_page: t2,
    is_first: t3,
    is_last: t4,
    configured_width,
    resizable: t5,
    column_index,
    stored_width,
    is_any_resizing: t6,
    onResizeStart,
    onResize,
    onResizeEnd
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const full_page = t2 === undefined ? false : t2;
  const is_first = t3 === undefined ? false : t3;
  const is_last = t4 === undefined ? false : t4;
  const resizable = t5 === undefined ? false : t5;
  const is_any_resizing = t6 === undefined ? false : t6;
  const [wrapper_node, setWrapperNode] = useState(null);
  const [original_width, setOriginalWidth] = useState(configured_width);
  const [is_resizing, setIsResizing] = useState(false);
  const resize_state_ref = useRef(null);
  const t7 = disabled || is_any_resizing;
  let t8;
  if ($[0] !== id || $[1] !== t7) {
    t8 = {
      id,
      disabled: t7
    };
    $[0] = id;
    $[1] = t7;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t8);
  let t10;
  let t9;
  if ($[3] !== configured_width || $[4] !== original_width || $[5] !== wrapper_node) {
    t9 = () => {
      if (!configured_width && wrapper_node && !original_width) {
        setOriginalWidth(wrapper_node.offsetWidth);
      }
    };
    t10 = [original_width, configured_width, wrapper_node];
    $[3] = configured_width;
    $[4] = original_width;
    $[5] = wrapper_node;
    $[6] = t10;
    $[7] = t9;
  } else {
    t10 = $[6];
    t9 = $[7];
  }
  useEffect(t9, t10);
  let t11;
  if ($[8] !== setNodeRef) {
    t11 = node => {
      setWrapperNode(node);
      setNodeRef(node);
    };
    $[8] = setNodeRef;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const set_refs = t11;
  let t12;
  if ($[10] !== column_index || $[11] !== onResize) {
    t12 = e => {
      if (!resize_state_ref.current || column_index === undefined) {
        return;
      }
      const {
        start_x,
        start_width,
        raf_id
      } = resize_state_ref.current;
      if (raf_id !== null) {
        cancelAnimationFrame(raf_id);
      }
      resize_state_ref.current.raf_id = requestAnimationFrame(() => {
        const delta = e.clientX - start_x;
        const new_width = Math.min(MAX_STORED_WIDTH, Math.max(MIN_STORED_WIDTH, start_width + delta));
        onResize?.(column_index, new_width);
        resize_state_ref.current.raf_id = null;
      });
    };
    $[10] = column_index;
    $[11] = onResize;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const handlePointerMove = t12;
  let handlePointerUp;
  if ($[13] !== column_index || $[14] !== handlePointerMove || $[15] !== onResizeEnd) {
    handlePointerUp = e_0 => {
      if (!resize_state_ref.current || column_index === undefined) {
        return;
      }
      const {
        start_x: start_x_0,
        start_width: start_width_0,
        raf_id: raf_id_0
      } = resize_state_ref.current;
      if (raf_id_0 !== null) {
        cancelAnimationFrame(raf_id_0);
      }
      const delta_0 = e_0.clientX - start_x_0;
      const final_width = Math.min(MAX_STORED_WIDTH, Math.max(MIN_STORED_WIDTH, start_width_0 + delta_0));
      setIsResizing(false);
      resize_state_ref.current = null;
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
      onResizeEnd?.(column_index, final_width);
    };
    $[13] = column_index;
    $[14] = handlePointerMove;
    $[15] = onResizeEnd;
    $[16] = handlePointerUp;
  } else {
    handlePointerUp = $[16];
  }
  let t13;
  if ($[17] !== column_index || $[18] !== handlePointerMove || $[19] !== handlePointerUp || $[20] !== onResizeStart || $[21] !== stored_width) {
    t13 = e_1 => {
      if (column_index === undefined || stored_width === undefined) {
        return;
      }
      e_1.stopPropagation();
      e_1.preventDefault();
      setIsResizing(true);
      resize_state_ref.current = {
        start_x: e_1.clientX,
        start_width: stored_width,
        raf_id: null
      };
      onResizeStart?.(column_index);
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    };
    $[17] = column_index;
    $[18] = handlePointerMove;
    $[19] = handlePointerUp;
    $[20] = onResizeStart;
    $[21] = stored_width;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const handleResizePointerDown = t13;
  let t14;
  if ($[23] !== handlePointerMove || $[24] !== handlePointerUp) {
    t14 = () => () => {
      if (resize_state_ref.current?.raf_id != null) {
        cancelAnimationFrame(resize_state_ref.current.raf_id);
      }
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
    $[23] = handlePointerMove;
    $[24] = handlePointerUp;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== handlePointerMove || $[27] !== handlePointerUp) {
    t15 = [handlePointerMove, handlePointerUp];
    $[26] = handlePointerMove;
    $[27] = handlePointerUp;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  useEffect(t14, t15);
  if (disabled) {
    let t16;
    if ($[29] !== children) {
      t16 = <>{children}</>;
      $[29] = children;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    return t16;
  }
  const t16 = isDragging ? original_width : undefined;
  let t17;
  if ($[31] !== transform) {
    t17 = CSS.Transform.toString(transform);
    $[31] = transform;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17 || $[34] !== transition) {
    t18 = {
      transform: t17,
      transition
    };
    $[33] = t17;
    $[34] = transition;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== attributes || $[37] !== isDragging || $[38] !== listeners) {
    t19 = <DragHandle $is_dragging={isDragging} {...attributes} {...listeners} />;
    $[36] = attributes;
    $[37] = isDragging;
    $[38] = listeners;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== children || $[41] !== full_page || $[42] !== is_first || $[43] !== is_last) {
    t20 = <ContentWrapper $full_page={full_page} $is_first={is_first} $is_last={is_last}>{children}</ContentWrapper>;
    $[40] = children;
    $[41] = full_page;
    $[42] = is_first;
    $[43] = is_last;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== handleResizePointerDown || $[46] !== is_resizing || $[47] !== resizable) {
    t21 = resizable && <ResizeHandle $is_resizing={is_resizing} onPointerDown={handleResizePointerDown} />;
    $[45] = handleResizePointerDown;
    $[46] = is_resizing;
    $[47] = resizable;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== isDragging || $[50] !== set_refs || $[51] !== t16 || $[52] !== t18 || $[53] !== t19 || $[54] !== t20 || $[55] !== t21) {
    t22 = <DraggableHeaderWrapper ref={set_refs} $is_dragging={isDragging} $width={t16} style={t18}>{t19}{t20}{t21}</DraggableHeaderWrapper>;
    $[49] = isDragging;
    $[50] = set_refs;
    $[51] = t16;
    $[52] = t18;
    $[53] = t19;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  return t22;
};
export default DraggableColumnHeader;